import React from 'react';
import * as R from 'ramda';
// components
import { TextComponent } from '../../components/text';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
// feature rate
import { telRateInitFields } from './driver/settings';
import { cloRateInitFields } from './customer/settings';
import { carrierRateInitFields } from './carrier/settings';
// ui
import { Flex } from '../../ui';
//////////////////////////////////////////////////

export const isActiveTabPrimaryDriver = R.equals('primary');

export const isActiveTabTeamDriver = R.equals('team');

export const isActiveTabVendor = R.equals('vendor');

const getPackageTypeAndTotalPickupQuantity = (totalQuantities) => {
  if (G.isNilOrEmpty(totalQuantities)) return null;

  const packageTypes = R.keys(totalQuantities);

  const packageType = G.ifElse(
    R.equals(R.length(R.uniq(packageTypes)), 1),
    R.head(packageTypes),
    GC.ITEM_QUANTITY_UNIT_PIECES,
  );

  return {
    [GC.FIELD_PACKAGE_TYPE]: packageType,
    [GC.FIELD_TOTAL_PICKUP_QUANTITY]: R.sum(R.values(totalQuantities)),
  };
};

export const getTotalQuantitiesFromValues = (values, initialTotalQuantities) => {
  const packageType = R.prop(GC.FIELD_PACKAGE_TYPE, values);

  if (G.isNilOrEmpty(packageType)) return initialTotalQuantities;

  return R.mergeRight(
    R.or(initialTotalQuantities, {}),
    { [packageType]: R.prop(GC.FIELD_TOTAL_PICKUP_QUANTITY, values) },
  );
};

export const getDefaultsFromValues = (values) => {
  const mode = R.pathOr('', [GC.FIELD_MODE], values);
  const currency = R.pathOr('', [GC.FIELD_CURRENCY], values);
  const serviceType = R.pathOr('', [GC.FIELD_SERVICE_TYPE], values);
  const serviceDays = R.pathOr('', [GC.FIELD_SERVICE_DAYS], values);

  return { mode, currency, serviceType, serviceDays };
};

const mapInitCharges = (charges, isEditMode) => R.map(
  (item) => {
    const toUse = G.ifElse(
      G.isTrue(isEditMode),
      R.assoc(GC.FIELD_GL_CODE, R.path([GC.FIELD_GL_CODE, GC.FIELD_DROPDOWN_OPTION_GUID], item), item),
      R.omit(GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR_2, item),
    );

    return R.assoc(GC.FIELD_ID, R.or(G.getGuidFromObject(item), G.genShortId()), toUse);
  },
  G.addOneFuelChargeToCharges(charges),
);

const invoiceChargesFilter = (isInvoice, charge) => {
  if (isInvoice) {
    const internalExpense = R.prop(GC.FIELD_CHARGE_INTERNAL_EXPENSE, charge);

    return R.not(internalExpense);
  }

  return true;
};

export const getDriverInitCharges = (charges, props) => {
  const { isInvoice, isEditMode, sharedAccessorialsApplyTo, values: { transportationMode } } = props;

  const filteredShared = G.filterChargesByModeOrNoMode(R.or(sharedAccessorialsApplyTo, []), transportationMode);

  if (G.isNilOrEmpty(charges)) return R.concat(G.createMainAndFuelCharges(), filteredShared);

  const mapped = mapInitCharges(charges, isEditMode);

  return R.filter((item) => invoiceChargesFilter(isInvoice, item), mapped);
};

export const makeResultObjectOnChangeDriver = (
  driver,
  secondaryDriver,
  props,
  isUpdate,
) => {
  const { values, availTrucks, availTrailers, preselectedTrailers } = props;

  const { mode, currency, serviceType, serviceDays } = getDefaultsFromValues(values);

  const assignment = G.getPropFromObject('assignment', driver);
  const truckGuid = G.getPropFromObject(GC.FIELD_TRUCK_GUID, assignment);

  const secondaryDriverGuid = G.getGuidFromObject(secondaryDriver);

  const secondaryDriverCharges = G.ifElse(
    G.isNotNilAndNotEmpty(secondaryDriverGuid),
    getDriverInitCharges(R.path([GC.FIELD_CHARGES], secondaryDriver), props),
    null,
  );

  const result = {
    [GC.FIELD_SECONDARY_DRIVER_CHARGES]: secondaryDriverCharges,
    [GC.FIELD_CURRENCY]: R.pathOr(currency, [GC.FIELD_CURRENCY], driver),
    [GC.FIELD_PRIMARY_DRIVER_GUID]: R.pathOr(null, [GC.FIELD_GUID], driver),
    [GC.FIELD_SECONDARY_DRIVER_GUID]: G.fromUndefinedToNull(secondaryDriverGuid),
    [GC.FIELD_DISTANCE_TO_START]: R.pathOr(0, [GC.FIELD_DISTANCE_TO_START], driver),
    [GC.FIELD_SERVICE_DAYS]: R.pathOr(serviceDays, [GC.FIELD_SERVICE_DAYS], driver),
    [GC.FIELD_MODE]: R.pathOr(mode, [GC.FIELD_MODE, GC.FIELD_DROPDOWN_OPTION_GUID], driver),
    [GC.FIELD_PRIMARY_DRIVER_CHARGES]: getDriverInitCharges(R.path([GC.FIELD_CHARGES], driver), props),
    [GC.FIELD_SERVICE_TYPE]: R.pathOr(serviceType, [GC.FIELD_SERVICE_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], driver),
  };

  if (R.not(isUpdate)) {
    if (R.any(R.propEq(truckGuid, GC.FIELD_GUID), availTrucks)) {
      result.truckGuid = truckGuid;
    }

    const valueTrailerGuids = R.prop(GC.FIELD_TRAILER_GUIDS, values);
    const preselectedTrailerGuids = R.map(G.getGuidFromObject, R.or(preselectedTrailers, []));

    const assignmentTrailerGuids = R.filter(
      (trailerGuid) => R.any((trailer) => (
        R.and(
          R.propEq(trailerGuid, GC.FIELD_GUID, trailer),
          R.not(R.prop(GC.FIELD_DISABLED_BY_SAFETY_SHUTOFF, trailer)),
        )
      ), availTrailers),
      R.pathOr([], [GC.FIELD_TRAILER_GUIDS], assignment),
    );

    const allFromPreselected = G.isNilOrEmpty(preselectedTrailerGuids) ? false : R.all(
      (guid) => R.includes(guid, preselectedTrailerGuids),
      R.or(valueTrailerGuids, []),
    );

    const trailerGuids = R.or(allFromPreselected, R.isEmpty(assignmentTrailerGuids)) ?
      valueTrailerGuids : assignmentTrailerGuids;

    result.trailerGuids = trailerGuids;
  }

  return result;
};

export const makeResultObjectOnRemoveDriver = () => ({
  [GC.FIELD_TRUCK_GUID]: null,
  [GC.FIELD_TRAILER_GUIDS]: [],
  [GC.FIELD_DISTANCE_TO_START]: 0,
  [GC.FIELD_DEADHEAD_DISTANCE]: 0,
  [GC.FIELD_PRIMARY_DRIVER_GUID]: null,
  [GC.FIELD_START_POINT_LOCATION]: null,
  [GC.FIELD_FINISH_POINT_LOCATION]: null,
  [GC.FIELD_FLEET_VENDOR_CHARGES]: null,
  [GC.FIELD_SECONDARY_DRIVER_GUID]: null,
  [GC.FIELD_SECONDARY_DRIVER_CHARGES]: null,
  [GC.FIELD_PRIMARY_DRIVER_CHARGES]: G.createMainAndFuelCharges(),
});

export const getVendorRateData = (props) => {
  const { values, vendorRates, truckFleetVendor } = props;

  const truckGuid = R.prop(GC.FIELD_TRUCK_GUID, values);
  const vendorGuid = G.getGuidFromObject(truckFleetVendor);

  const vendorRate = R.findLast(({ guid, truckGuid: compensationTruckGuid }) => {
    if (R.or(G.isNilOrEmpty(truckGuid), G.isNilOrEmpty(compensationTruckGuid))) {
      return R.equals(guid, vendorGuid);
    }

    return R.and(
      R.equals(guid, vendorGuid),
      R.equals(truckGuid, compensationTruckGuid),
    );
  }, vendorRates);

  const price = R.pathOr({}, ['price'], vendorRate);
  const charges = R.pathOr([], ['charges'], vendorRate);

  return {
    price,
    charges,
  };
};

const getVendorInitCharges = (charges, props) => {
  const { isInvoice, isEditMode, sharedAccessorialsApplyTo, values: { transportationMode } } = props;

  const filteredShared = G.filterChargesByModeOrNoMode(R.or(sharedAccessorialsApplyTo, []), transportationMode);

  if (G.isNilOrEmpty(charges)) return R.concat(G.createMainAndFuelCharges(), filteredShared);

  const mapped = mapInitCharges(charges, isEditMode);

  return R.filter((item) => invoiceChargesFilter(isInvoice, item), mapped);
};

export const makeResultObjectOnChangeVendorRate = (props) => {
  const { values } = props;

  const { mode, currency, serviceType, serviceDays } = getDefaultsFromValues(values);

  const { price, charges } = getVendorRateData(props);

  const chargesToUse = getVendorInitCharges(charges, props);

  const result = {
    [GC.FIELD_FLEET_VENDOR_CHARGES]: chargesToUse,
    [GC.FIELD_CURRENCY]: R.pathOr(currency, [GC.FIELD_CURRENCY], price),
    [GC.FIELD_CUSTOMER_TOTAL]: G.getPropFromObject(GC.FIELD_CUSTOMER_TOTAL, price),
    [GC.FIELD_SERVICE_DAYS]: R.pathOr(serviceDays, [GC.FIELD_SERVICE_DAYS], price),
    [GC.FIELD_MODE]: R.pathOr(mode, [GC.FIELD_MODE, GC.FIELD_DROPDOWN_OPTION_GUID], price),
    [GC.FIELD_SERVICE_TYPE]: R.pathOr(serviceType, [GC.FIELD_SERVICE_TYPE, GC.FIELD_DROPDOWN_OPTION_GUID], price),
  };

  return result;
};

export const getFormGroupTitles = ({ isEditMode }) => G.ifElse(
  isEditMode,
  G.getWindowLocale('titles:edit', 'Edit'),
  G.getWindowLocale('titles:add', 'Add'),
);

export const getTotalsFromValues = (values) => (
  R.pick(GC.GROUPED_FIELDS.TRIP_TOTALS_ARR, values)
);

export const setDefaultCustomerRateValues = (props) => {
  const {
    totalWeight,
    totalDistance,
    initialValues,
    defaultCurrency,
    defaultUomFields,
    asyncTotalDistance,
    totalPickupQuantity,
  } = props;

  if (G.isNotNilAndNotEmpty(initialValues)) {
    const initValues = G.mapDropdownsObjectInEntity(
      GC.FIELD_DROPDOWN_OPTION_GUID,
      [GC.FIELD_SERVICE_TYPE, GC.FIELD_MODE],
      initialValues,
    );
    const initWithDist = R.mergeRight(cloRateInitFields, asyncTotalDistance);
    const initWithWeight = R.mergeRight(initWithDist, totalWeight);

    return R.mergeRight(initWithWeight, initValues);
  }

  return G.setDefaultFieldsToRateInvoiceFormik({
    totalWeight,
    defaultCurrency,
    defaultUomFields,
    totalPickupQuantity,
    formikInitFields: cloRateInitFields,
    totalDistance: R.mergeRight(asyncTotalDistance, totalDistance),
  });
};

export const setDefaultCustomerRateValuesWithAsync = (props) => {
  const { totalWeight, totalDistance, initialValues, asyncConfigs, asyncTotalDistance } = props;

  if (G.isNotNilAndNotEmpty(initialValues)) {
    const initValues = G.mapDropdownsObjectInEntity(
      GC.FIELD_DROPDOWN_OPTION_GUID,
      [GC.FIELD_SERVICE_TYPE, GC.FIELD_MODE],
      initialValues,
    );
    const initWithDist = R.mergeRight(cloRateInitFields, asyncTotalDistance);
    const initWithWeight = R.mergeRight(initWithDist, totalWeight);

    return R.mergeRight(initWithWeight, initValues);
  }

  return G.setDefaultFieldsToRateInvoiceAsyncFormik({
    totalWeight,
    formikInitFields: cloRateInitFields,
    defaultUomFields: G.getDefaultUomFields(asyncConfigs),
    totalDistance: R.mergeRight(asyncTotalDistance, totalDistance),
    currency: G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, asyncConfigs),
    mode: G.getConfigValueFromStore(GC.CLO_GENERAL_RATE_TRANSPORTATION_MODE, asyncConfigs),
    serviceType: G.getConfigValueFromStore(GC.CLO_GENERAL_RATE_SERVICE_TYPE, asyncConfigs),
  });
};

// TODO: check quantities
export const getTotalValuesFromRateInfo = (rateInfo) => {
  const totalVolume = R.pick([
    GC.FIELD_TOTAL_TRIP_VOLUME,
    GC.FIELD_TOTAL_TRIP_VOLUME_UOM,
  ], R.or(rateInfo, {}));

  const totalWeight = R.pick([
    GC.FIELD_TOTAL_TRIP_WEIGHT,
    GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
  ], R.or(rateInfo, {}));

  const totalDistance = R.pick([
    GC.FIELD_TOTAL_TRIP_DISTANCE,
    GC.FIELD_TOTAL_TRIP_DISTANCE_UOM,
  ], R.or(rateInfo, {}));

  return {
    totalVolume,
    totalWeight,
    totalDistance,
    totalPickupQuantity: getPackageTypeAndTotalPickupQuantity(R.prop(GC.FIELD_TOTAL_QUANTITIES, rateInfo)),
  };
};

const getInitialCustomerTotal = (rateInfo, orderRateInfo, defaultCurrency) => {
  // TODO: check if we have this total in normalizedCurrency
  const rateInfoTotal = G.getPropFromObject2(GC.FIELD_TOTAL_CUSTOMER_RATE, rateInfo, null);

  if (G.isNotNilAndNotEmpty(rateInfoTotal)) {
    const { normalizedCurrency } = rateInfo;

    const convertedTotal = G.convertCurrencyByExchangeRateToFloatOrNull(
      rateInfoTotal,
      normalizedCurrency,
      defaultCurrency,
    );

    return convertedTotal;
  }

  const orderRateTotal = G.getPropFromObject2(GC.FIELD_TOTAL_CUSTOMER_RATE, orderRateInfo, null);

  if (G.isNotNilAndNotEmpty(orderRateTotal)) {
    const { currency } = orderRateInfo;

    const convertedTotal = G.convertCurrencyByExchangeRateToFloatOrNull(
      orderRateTotal,
      currency,
      defaultCurrency,
    );

    return convertedTotal;
  }

  return null;
};

export const setDefaultDriverRateValues = (props) => {
  const {
    rateInfo,
    asyncConfigs,
    initialValues,
    orderRateInfo,
    fallbackRateInfo,
    transportationMode,
    preselectedTrailers,
  } = props;

  if (G.isNotNilAndNotEmpty(initialValues)) {
    const assignment = R.path([GC.SYSTEM_OBJECT_FLEET_ASSIGNMENT], initialValues);

    if (G.isNotNilAndNotEmpty(assignment)) {
      const assignmentFields = R.pick(
        GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR,
        assignment,
      );

      const merged = R.mergeRight(initialValues, assignmentFields);

      const initValues = G.mapDropdownsObjectInEntity(
        GC.FIELD_DROPDOWN_OPTION_GUID,
        [GC.FIELD_SERVICE_TYPE, GC.FIELD_MODE, GC.FIELD_MARGIN_VIOLATION_REASON],
        merged,
      );

      return R.mergeRight(telRateInitFields, initValues);
    }

    const values = G.mapDropdownsObjectInEntity(
      GC.FIELD_DROPDOWN_OPTION_GUID,
      [GC.FIELD_SERVICE_TYPE, GC.FIELD_MODE, GC.FIELD_MARGIN_VIOLATION_REASON],
      initialValues,
    );

    return R.mergeRight(telRateInitFields, values);
  }

  const mode = R.or(G.getConfigValueFromStore(GC.TEL_DRIVER_RATE_DEFAULT_MODE, asyncConfigs), transportationMode);
  const defaultUomFields = G.getDefaultUomFields(asyncConfigs);
  const defaultCurrency = G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, asyncConfigs);
  const serviceType = G.getConfigValueFromStore(GC.TEL_DRIVER_RATE_DEFAULT_SERVICE_TYPE, asyncConfigs);

  const customerTotal = getInitialCustomerTotal(rateInfo, orderRateInfo, defaultCurrency);

  const trailerGuids = G.isNotNilAndNotEmpty(preselectedTrailers) ?
    R.map(G.getGuidFromObject, preselectedTrailers) : null;

  const rateWeight = R.prop(GC.FIELD_TOTAL_TRIP_WEIGHT, rateInfo);
  const rateWeightUom = R.prop(GC.FIELD_TOTAL_TRIP_WEIGHT_UOM, rateInfo);
  const fallbackWeight = R.prop(GC.FIELD_TOTAL_TRIP_WEIGHT, fallbackRateInfo);
  const fallbackWeightUom = R.prop(GC.FIELD_TOTAL_TRIP_WEIGHT_UOM, fallbackRateInfo);

  const totalTripWeight = R.isNil(rateWeight) || rateWeight === '' ? fallbackWeight : rateWeight;

  const totalTripWeightUom = R.isNil(rateWeightUom) || rateWeightUom === ''
    ? fallbackWeightUom
    : rateWeightUom;

  const rateInfoForTotals = {
    ...fallbackRateInfo,
    ...rateInfo,
    [GC.FIELD_TOTAL_TRIP_WEIGHT]: totalTripWeight,
    [GC.FIELD_TOTAL_TRIP_WEIGHT_UOM]: totalTripWeightUom,
  };

  return G.setDefaultFieldsToRateInvoiceFormik({
    ...getTotalValuesFromRateInfo(rateInfoForTotals),
    mode,
    serviceType,
    customerTotal,
    defaultCurrency,
    defaultUomFields,
    formikInitFields: R.mergeRight(
      telRateInitFields,
      { trailerGuids, [GC.FIELD_START_POINT_LOCATION]: null, [GC.FIELD_FINISH_POINT_LOCATION]: null },
    ),
  });
};

export const calcDMTotalRate = (charges) => {
  if (G.isNotNilAndNotEmpty(charges)) {
    return G.calculateChargesTotal(charges);
  }

  return 0;
};

export const filterDriversOptions = (props) => {
  const primary = R.path(['values', GC.FIELD_PRIMARY_DRIVER_GUID], props);
  const secondary = R.path(['values', GC.FIELD_SECONDARY_DRIVER_GUID], props);
  const fleetOptions = R.clone(props.fleetOptionsForSelect);

  if (G.isNotNilAndNotEmpty(secondary)) {
    fleetOptions.primaryDriversOptions = R.filter(
      (option) => G.notEquals(option.value, secondary),
      fleetOptions.primaryDriversOptions,
    );
  }

  if (G.isNotNilAndNotEmpty(primary)) {
    fleetOptions.secondaryDriversOptions = R.filter(
      (option) => G.notEquals(option.value, primary),
      fleetOptions.secondaryDriversOptions,
    );
  }

  return fleetOptions;
};

export const sortTruckOptions = (options, vendorGuid) => {
  if (R.isNil(vendorGuid)) return options;

  const { trucksOptions } = options;

  const vendorTrucks = R.filter(
    R.propEq(vendorGuid, 'vendorGuid'),
    trucksOptions,
  );

  const notVendorTrucks = R.filter(
    G.notPropEq(vendorGuid, 'vendorGuid'),
    trucksOptions,
  );

  return R.assoc('trucksOptions', R.concat(vendorTrucks, notVendorTrucks), options);
};

export const getDeletedDriversOptions = ({ fleetAssignment }, fleetOptions) => {
  const addDriverOption = (driverType, options = []) => {
    if (R.not(R.path([driverType, GC.FIELD_DELETED], fleetAssignment))) return options;

    return R.append(
      {
        [GC.FIELD_VALUE]: R.path([driverType, GC.FIELD_GUID], fleetAssignment),
        [GC.FIELD_LABEL]: R.path([driverType, GC.FIELD_FULL_NAME], fleetAssignment),
      },
      options,
    );
  };

  const { primaryDriversOptions, secondaryDriversOptions } = fleetOptions;

  return R.mergeRight(fleetOptions, {
    primaryDriversOptions: addDriverOption(GC.FIELD_PRIMARY_DRIVER, primaryDriversOptions),
    secondaryDriversOptions: addDriverOption(GC.FIELD_SECONDARY_DRIVER, secondaryDriversOptions),
  });
};

export const disableDriverField = (field, props) => {
  const { values, telStatus, optionsForSelect } = props;

  if (R.propEq(GC.FIELD_PRIMARY_DRIVER_GUID, 'fieldName', field)) {
    return R.assoc('disabled', true, field);
  }

  if (R.propEq(GC.FIELD_TRAILER_GUIDS, 'fieldName', field)) {
    const manageConfig = G.getAmousBranchConfigFromWindow(GC.TRAILER_MANAGE_TRAILERS_FROM);

    const selectedTrailers = R.pathOr([], [GC.FIELD_FLEET_ASSIGNMENT, GC.FIELD_TRAILERS], values);

    const selectedTrailersOptions = G.isNotEmpty(selectedTrailers)
      ? G.mapUnitIdGuidObjectPropsToLabelValueObject(selectedTrailers)
      : R.map(
        (guid) => ({
          [GC.FIELD_VALUE]: guid,
          [GC.FIELD_LABEL]: G.getWindowLocale('titles:unknown', 'Unknown'),
        }),
        R.pathOr([], [GC.FIELD_FLEET_ASSIGNMENT, GC.FIELD_TRAILER_GUIDS], values),
      );

    const trailersOptions = R.pathOr([], ['trailersOptions'], optionsForSelect);

    const options = G.isNotEmpty(selectedTrailersOptions)
      ? R.uniqBy(R.prop(GC.FIELD_VALUE), R.concat(trailersOptions, selectedTrailersOptions))
      : trailersOptions;

    return {
      ...field,
      options,
      disabled: R.equals(manageConfig, ENUMS.ENUM_EVENTS),
    };
  }

  if (R.and(G.isNotNilAndNotEmpty(telStatus), R.propEq(GC.FIELD_SECONDARY_DRIVER_GUID, 'fieldName', field))) {
    const sequence = GC.TEL_STATUS_SEQUENCE_MAP[telStatus];
    const disabled = R.gt(sequence, 7);

    return R.assoc('disabled', disabled, field);
  }

  if (R.and(G.isNotNilAndNotEmpty(telStatus), R.propEq(GC.FIELD_TRUCK_GUID, 'fieldName', field))) {
    const sequence = GC.TEL_STATUS_SEQUENCE_MAP[telStatus];

    if (R.lt(sequence, 8)) return field;

    return {
      ...field,
      type: 'customComponent',
      Component: (props) => {
        const { values } = props;

        const truckName = R.path(['fleetAssignment', 'truck', 'unitId'], values);

        const vendorName = R.path(['fleetAssignment', 'fleetVendor', 'name'], values);

        const vandorTitle = G.getWindowLocale('titles:vendor', 'Vendor');

        const text = G.isNilOrEmpty(vendorName) ? truckName : `${truckName} (${vandorTitle}: ${vendorName})`;

        return (
          <Flex
            height={36}
            borderRadius='5px'
            border='1px solid'
            borderColor={G.getTheme('colors.dark.grey')}
          >
            <TextComponent
              ml='8px'
              title={text}
              display='block'
              maxWidth='100%'
              withEllipsis={true}
              color={G.getTheme('colors.nearGray')}
            >
              {text}
            </TextComponent>
          </Flex>
        );
      },
    };
  }

  return field;
};

export const mapDriversToOptions = (data, primary) => (
  R.map(
    (item) => {
      const driverTotalValue = R.prop('normalizedTotalOrTotal', item);

      const getPrimaryDriverCurrency = R.pathOr(
        G.getPropFromObject(GC.FIELD_CURRENCY, item),
        [GC.SYSTEM_OBJECT_NORMALIZED_TOTAL, GC.FIELD_CURRENCY],
        item,
      );

      const vendorName = R.path([GC.SYSTEM_OBJECT_FLEET_VENDOR, 'name'], item);

      const vendorString = G.ifElse(
        G.isNotNilAndNotEmpty(vendorName),
        `, ${G.getWindowLocale('titles:vendor', 'Vendor')}: ${vendorName}`,
        '',
      );

      const location = R.or(
        R.path(['precedingLocation'], item),
        R.path(['currentLocation'], item),
      );

      const lnl = G.ifElse(
        G.isNotNilAndNotEmpty(location),
        `${G.getWindowLocale('titles:lnl', 'LNL')}: ${G.concatLocationFields(location)}`,
        '',
      );

      const distanceValue = R.pathOr('', [GC.FIELD_DISPLAY_DISTANCE_TO_START], item);

      const distanceString = G.ifElse(
        G.isNotNilAndNotEmpty(distanceValue),
        `${G.getWindowLocale('titles:distance-to-start', 'Distance To Start')}:
          ${distanceValue} ${G.getUomLocale(R.prop(GC.FIELD_DISPLAY_DISTANCE_TO_START_UOM, item), 'abbreviation')}`,
        '',
      );

      const teamTotalValue = R.prop('teamTotal', item);

      const teamTotal = G.ifElse(
        G.isAllTrue(
          G.isNotNilAndNotEmpty(R.path(['assignment', GC.FIELD_SECONDARY_DRIVER_GUID], item)),
          G.isNotNilAndNotEmpty(teamTotalValue),
          G.isTrue(primary),
        ),
        `${G.getWindowLocale('titles:team-total', 'Team Total')}: $${teamTotalValue},`,
        '',
      );

      const driverTotal = G.ifElse(
        G.isNotNilAndNotEmpty(driverTotalValue),
        `${G.getWindowLocale('titles:driver-total', 'Driver Total')}: ${
          G.getCurrencySymbol(getPrimaryDriverCurrency)}${driverTotalValue}`,
        '',
      );

      return {
        value: R.prop(GC.FIELD_GUID, item),
        label: `${R.prop(GC.FIELD_FIRST_NAME, item)} ${R.prop(GC.FIELD_LAST_NAME, item)}${vendorString}`,
        lnl,
        distance: distanceString,
        total: `${teamTotal}${driverTotal}`,
        vendorGuid: R.pathOr(null, [GC.SYSTEM_OBJECT_FLEET_VENDOR, GC.FIELD_GUID], item),
      };
    },
    data,
  )
);

export const mapAvailableDriversToOptions = (data, primary) => (
  R.map(
    (item) => {
      const driverTotalValue = R.prop('total', item);
      const teamTotalValue = R.prop('teamTotal', item);
      const disabledBySafetyShutoff = R.prop(GC.FIELD_DISABLED_BY_SAFETY_SHUTOFF, item);

      const vendorName = R.path([GC.SYSTEM_OBJECT_FLEET_VENDOR, 'name'], item);

      const vendorString = G.ifElse(
        G.isNotNilAndNotEmpty(vendorName),
        `, ${G.getWindowLocale('titles:vendor', 'Vendor')}: ${vendorName}`,
        '',
      );

      const location = R.or(
        R.path(['precedingLocation'], item),
        R.path(['currentLocation'], item),
      );

      const lnl = G.ifElse(
        G.isNotNilAndNotEmpty(location),
        `${G.getWindowLocale('titles:lnl', 'LNL')}: ${G.concatLocationFields(location)}`,
        '',
      );

      const teamTotal = G.ifElse(
        G.isAllTrue(
          G.isNotNilAndNotEmpty(R.path(['assignment', GC.FIELD_SECONDARY_DRIVER_GUID], item)),
          G.isNotNilAndNotEmpty(teamTotalValue),
          G.isTrue(primary),
        ),
        `${G.getWindowLocale('titles:team-total', 'Team Total')}: $${teamTotalValue},`,
        '',
      );

      const driverTotal = G.ifElse(
        G.isNotNilAndNotEmpty(driverTotalValue),
        `${G.getWindowLocale('titles:driver-total', 'Driver Total')}: $${driverTotalValue}`,
        '',
      );

      return {
        lnl,
        disabledBySafetyShutoff,
        value: R.prop(GC.FIELD_GUID, item),
        isDisabled: disabledBySafetyShutoff,
        total: `${teamTotal}${driverTotal}`,
        vendorGuid: R.pathOr(null, [GC.SYSTEM_OBJECT_FLEET_VENDOR, GC.FIELD_GUID], item),
        label: `${R.prop(GC.FIELD_FIRST_NAME, item)} ${R.prop(GC.FIELD_LAST_NAME, item)}${vendorString}`,
      };
    },
    data,
  )
);

const getEquipmentLabel = (item) => {
  const unitId = R.path(['unitId'], item);
  const vendorName = R.path(['fleetVendor', 'name'], item);

  if (G.isNotNilAndNotEmpty(vendorName)) {
    return `${unitId} (${G.getWindowLocale('titles:vendor', 'Vendor')}: ${vendorName})`;
  }

  return unitId;
};

export const mapEquipmentsToOptions = (data) => (
  R.map(
    (item) => {
      const disabledBySafetyShutoff = R.prop(GC.FIELD_DISABLED_BY_SAFETY_SHUTOFF, item);

      return {
        disabledBySafetyShutoff,
        label: getEquipmentLabel(item),
        value: R.prop(GC.FIELD_GUID, item),
        isDisabled: disabledBySafetyShutoff,
        vendorGuid: R.path(['fleetVendor', GC.FIELD_GUID], item),
      };
    },
    data,
  )
);

export const mapAvailableTrailersToOptions = (data) => (
  R.map(
    (item) => {
      const { guid, unitId, distance, distanceUom, currentLocation } = item;
      const disabledBySafetyShutoff = R.prop(GC.FIELD_DISABLED_BY_SAFETY_SHUTOFF, item);

      const vendorName = R.path([GC.SYSTEM_OBJECT_FLEET_VENDOR, GC.FIELD_NAME], item);

      const vendorString = G.isNotNilAndNotEmpty(vendorName)
        ? ` (${G.getWindowLocale('titles:vendor', 'Vendor')}: ${vendorName})`
        : '';

      const lnl = G.isNotNilAndNotEmpty(currentLocation)
        ? `${G.getWindowLocale('titles:lnl', 'LNL')}: ${G.concatLocationFields(currentLocation)}`
        : '';

      const distanceString = G.isNotNilAndNotEmpty(distance)
        ? `${G.getWindowLocale('titles:distance-to-start', 'Distance To Start')}: ${
          G.mathRoundNumber(distance)} ${G.getUomLocale(distanceUom, 'abbreviation')}`
        : '';

      return {
        lnl,
        [GC.FIELD_VALUE]: guid,
        disabledBySafetyShutoff,
        isDisabled: disabledBySafetyShutoff,
        [GC.FIELD_DISTANCE]: distanceString,
        [GC.FIELD_LABEL]: `${unitId}${vendorString}`,
        [GC.FIELD_VENDOR_GUID]: R.pathOr(null, [GC.SYSTEM_OBJECT_FLEET_VENDOR, GC.FIELD_GUID], item),
      };
    },
    data,
  )
);

export const mapRateDataWithProperQuantity = (rateData) => {
  const mapCharges = R.map(
    R.when(
      (charge) => R.isNil(charge.quantity)
        && R.not(R.and(
          R.equals(charge.rateType, GC.CHARGE_RATE_TYPE_TIME),
          R.equals(charge.rateUnit, GC.CHARGE_RATE_UNIT_TYPE_HOUR),
        )),
      R.assoc(GC.FIELD_CHARGE_QUANTITY, 1),
    ),
  );

  return R.compose(
    R.indexBy(R.prop(GC.FIELD_GUID)),
    R.map((rate) => R.assoc(GC.FIELD_CHARGES, mapCharges(R.prop(GC.FIELD_CHARGES, rate)), rate)),
  )(rateData);
};

export const mapCarrierRatesWithProperQuantity = (carrierRates) => {
  const mapCharges = R.map(
    R.when(
      (charge) => R.isNil(charge.quantity)
        && R.not(R.and(
          R.equals(charge.rateType, GC.CHARGE_RATE_TYPE_TIME),
          R.equals(charge.rateUnit, GC.CHARGE_RATE_UNIT_TYPE_HOUR),
        )),
      R.assoc(GC.FIELD_CHARGE_QUANTITY, 1),
    ),
  );

  return R.map((rate) => R.assoc(
    GC.FIELD_CHARGES, mapCharges(R.prop(GC.FIELD_CHARGES, rate)),
    rate,
  ), carrierRates);
};

const getTotalsFromValuesForCarrierRate = (rateInfo, mockRateInfo) => {
  if (G.isNotNilAndNotEmpty(mockRateInfo)) {
    const totalTripVolume = R.or(
      R.path(['totalWeight', GC.FIELD_TOTAL_TRIP_VOLUME], mockRateInfo),
      G.getPropFromObject(GC.FIELD_TOTAL_TRIP_VOLUME, rateInfo),
    );
    const totalTripVolumeUom = R.or(
      R.path(['totalWeight', GC.FIELD_TOTAL_TRIP_VOLUME_UOM], mockRateInfo),
      G.getPropFromObject(GC.FIELD_TOTAL_TRIP_VOLUME_UOM, rateInfo),
    );
    const totalTripWeight = R.or(
      R.path(['totalWeight', GC.FIELD_TOTAL_TRIP_WEIGHT], mockRateInfo),
      G.getPropFromObject(GC.FIELD_TOTAL_TRIP_WEIGHT, rateInfo),
    );
    const totalTripWeightUom = R.or(
      R.path(['totalWeight', GC.FIELD_TOTAL_TRIP_WEIGHT_UOM], mockRateInfo),
      G.getPropFromObject(GC.FIELD_TOTAL_TRIP_WEIGHT_UOM, rateInfo),
    );
    const totalTripDistance = R.or(
      R.path(['totalWeight', GC.FIELD_TOTAL_TRIP_DISTANCE], mockRateInfo),
      G.getPropFromObject(GC.FIELD_TOTAL_TRIP_DISTANCE, rateInfo),
    );
    const totalTripDistanceUom = R.or(
      R.path(['totalWeight', GC.FIELD_TOTAL_TRIP_DISTANCE_UOM], mockRateInfo),
      G.getPropFromObject(GC.FIELD_TOTAL_TRIP_DISTANCE_UOM, rateInfo),
    );

    return {
      totalVolume: { totalTripVolume, totalTripVolumeUom },
      totalWeight: { totalTripWeight, totalTripWeightUom },
      totalDistance: { totalTripDistance, totalTripDistanceUom },
      totalPickupQuantity: R.propOr(
        getPackageTypeAndTotalPickupQuantity(R.prop(GC.FIELD_TOTAL_QUANTITIES, rateInfo)),
        'totalPickupQuantity',
        mockRateInfo,
      ),
    };
  }

  return getTotalValuesFromRateInfo(rateInfo);
};

// TODO: this helper has bug when rate info come up later then user choose carrier
// quick fixed with showing loader on available drivers
export const setDefaultCarrierRateValues = (props) => {
  const {
    rateInfo,
    mockRateInfo,
    asyncConfigs,
    initialValues,
    orderRateInfo,
    preselectedTrailers,
  } = props;

  if (G.isNotNilAndNotEmpty(initialValues)) {
    const initValues = G.mapDropdownsObjectInEntity(
      GC.FIELD_DROPDOWN_OPTION_GUID,
      [GC.FIELD_SERVICE_TYPE, GC.FIELD_MODE, GC.FIELD_MARGIN_VIOLATION_REASON],
      initialValues,
    );

    return R.mergeRight(carrierRateInitFields, initValues);
  }

  const mode = G.getConfigValueFromStore(GC.TEL_CARRIER_RATE_DEFAULT_MODE, asyncConfigs);

  const defaultCurrency = G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, asyncConfigs);

  const customerTotal = getInitialCustomerTotal(rateInfo, orderRateInfo, defaultCurrency);

  const fleetTrailerGuid = G.isNotNilAndNotEmpty(preselectedTrailers) ?
    R.compose(
      G.getGuidFromObject,
      R.head,
    )(preselectedTrailers) : null;

  return G.setDefaultFieldsToRateInvoiceFormik({
    ...getTotalsFromValuesForCarrierRate(rateInfo, mockRateInfo),
    mode,
    customerTotal,
    defaultCurrency,
    defaultUomFields: G.getDefaultUomFields(asyncConfigs),
    serviceType: G.getConfigValueFromStore(GC.TEL_CARRIER_RATE_DEFAULT_SERVICE_TYPE, asyncConfigs),
    formikInitFields: R.assocPath(
      [GC.FIELD_PREFIX_CARRIER_ASSIGNMENT, GC.FIELD_FLEET_TRAILER_GUID],
      fleetTrailerGuid,
      carrierRateInitFields,
    ),
  });
};

export const getChargesFromValues = (props) => {
  const { values, isCarrier, isInvoice, isEditMode, activeDriver, isServiceVendor } = props;

  if (R.and(isInvoice, isEditMode)) {
    return R.pathOr([], [GC.FIELD_CHARGES], values);
  }

  if (G.isTrue(isCarrier)) {
    return R.pathOr([], [GC.FIELD_CARRIER_RATE_CHARGES], values);
  }

  if (G.isTrue(isServiceVendor)) {
    return R.pathOr([], [GC.FIELD_CHARGES], values);
  }

  if (isActiveTabPrimaryDriver(activeDriver)) {
    return R.pathOr([], [GC.FIELD_PRIMARY_DRIVER_CHARGES], values);
  }

  if (isActiveTabTeamDriver(activeDriver)) {
    return R.pathOr([], [GC.FIELD_SECONDARY_DRIVER_CHARGES], values);
  }

  if (isActiveTabVendor(activeDriver)) {
    return R.pathOr([], [GC.FIELD_FLEET_VENDOR_CHARGES], values);
  }

  return [];
};

export const getChargesForRateTotalFromValues = (values) => {
  const fleetVendorCharges = R.path([GC.FIELD_FLEET_VENDOR_CHARGES], values);

  if (G.isNotNilAndNotEmpty(fleetVendorCharges)) {
    return fleetVendorCharges;
  }

  const chargesNames = [
    GC.FIELD_CHARGES,
    GC.FIELD_CARRIER_RATE_CHARGES,
    GC.FIELD_PRIMARY_DRIVER_CHARGES,
    GC.FIELD_SECONDARY_DRIVER_CHARGES,
  ];

  return R.reduce((acc, name) => (
    R.concat(acc, R.pathOr([], [name], values))
  ), [], chargesNames);
};

export const getChargesArrayNameFromProps = (props) => {
  const { isCarrier, isInvoice, isEditMode, activeDriver } = props;

  if (R.and(isInvoice, isEditMode)) {
    return GC.FIELD_CHARGES;
  }

  if (G.isTrue(isCarrier)) {
    return GC.FIELD_CARRIER_RATE_CHARGES;
  }

  if (isActiveTabPrimaryDriver(activeDriver)) {
    return GC.FIELD_PRIMARY_DRIVER_CHARGES;
  }

  if (isActiveTabTeamDriver(activeDriver)) {
    return GC.FIELD_SECONDARY_DRIVER_CHARGES;
  }

  if (isActiveTabVendor(activeDriver)) {
    return GC.FIELD_FLEET_VENDOR_CHARGES;
  }

  return GC.FIELD_CHARGES;
};

export const getMarginViolationData = (props) => {
  const {
    values,
    rateInfo,
    marginViolationRules,
  } = props;

  if (G.isNilOrEmpty(rateInfo)) {
    return { maxPayViolated: false, marginViolated: false, criticalMarginViolated: false };
  }

  const { currency, transportationMode } = values;

  const {
    routeTotalSpend,
    routeTotalIncome,
    normalizedCurrency,
    normalizedTripMaxPay,
  } = rateInfo;

  const charges = getChargesForRateTotalFromValues(values);

  const chargesTotal = G.convertCurrencyByExchangeRate(
    G.getCalcTelRateChargesTotal(charges, currency),
    currency,
    normalizedCurrency,
  );

  if (G.isNumber(normalizedTripMaxPay)) {
    return {
      marginViolated: false,
      criticalMarginViolated: false,
      maxPayViolated: R.lt(normalizedTripMaxPay, chargesTotal),
    };
  }

  const routeTransportationModes = R.pathOr([], ['routeTransportationModes'], rateInfo);
  const useTransportationModeRuleFromValues = R.or(
    R.isEmpty(routeTransportationModes),
    R.includes(transportationMode, routeTransportationModes),
  );
  const rule = R.pathOr(null, [transportationMode], marginViolationRules);

  const conditions = [
    R.isNil(rule),
    G.isNilOrEmpty(transportationMode),
    G.isNilOrEmpty(marginViolationRules),
    R.not(useTransportationModeRuleFromValues),
    R.and(G.isNotEmpty(routeTransportationModes), G.notContain(transportationMode, routeTransportationModes)),
  ];

  if (G.isAnyTrue(...conditions)) {
    return { maxPayViolated: false, marginViolated: false, criticalMarginViolated: false };
  }

  const { minMargin, criticalMargin } = rule;

  const totalSpend = R.add(R.or(routeTotalSpend, 0), R.or(chargesTotal, 0));

  let marginViolated = false;
  let criticalMarginViolated = false;
  const routeMargin = R.subtract(routeTotalIncome, totalSpend);

  if (R.lt(routeMargin, 0)) {
    marginViolated = G.ifElse(G.isNotNil(minMargin), true, false);
    criticalMarginViolated = G.ifElse(G.isNotNil(criticalMargin), true, false);
  } else {
    const divided = R.divide(routeMargin, routeTotalIncome);
    marginViolated = R.compose(
      R.gt(minMargin),
      R.multiply(100),
    )(divided);
    criticalMarginViolated = R.compose(
      R.gt(criticalMargin),
      R.multiply(100),
    )(divided);
  }

  return { marginViolated, criticalMarginViolated, maxPayViolated: false };
};

export const getTotalDistanceFromValues = (values) => {
  const { deadHeadDistance, deadHeadDistanceUom, totalTripDistance, totalTripDistanceUom } = values;

  const defaultUomSystem = G.getConfigGeneralUomCalcDefaultUomSystemFromWindow();
  const defaultUom = R.pathOr(GC.UOM_MILE, [defaultUomSystem], GC.uomSystemToDistanceUomMap);

  const total = G.milesKmsFromToAccordingSystemWithNullable(
    defaultUom,
    totalTripDistanceUom,
    totalTripDistance,
  );

  const deadhead = G.milesKmsFromToAccordingSystemWithNullable(
    defaultUom,
    deadHeadDistanceUom,
    deadHeadDistance,
  );

  return G.toFixed(R.add(total, deadhead));
};

export const mapDispatchersToOptions = (dispatchers) =>
  R.map(({ loginId, lastName, firstName }) => ({
    value: loginId,
    label: `${R.or(firstName, '')} ${R.or(lastName, '')} (${loginId})`,
  }), dispatchers);
